import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getMobileAppDesigns, type DesignAsset } from "@/services/localAssets";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

export const MobileAppSection = () => {
  const [designs, setDesigns] = useState<DesignAsset[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      setIsLoading(true);
      const mobileAppDesigns = getMobileAppDesigns();
      setDesigns(mobileAppDesigns);
    } catch (err) {
      console.error("Error fetching mobile app designs:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Image error handler
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn("Image failed to load:", e.currentTarget.src);
    e.currentTarget.src = "https://via.placeholder.com/300x300?text=Image+Not+Found";
  };

  return (
    <section className="min-h-screen py-20 px-4">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-3xl font-bold text-center mb-6"
      >
        Mobile Applications
      </motion.h2>

      <div className="flex justify-center mb-10">
        <Link 
          to="/mobile-app-designs" 
          className="bg-accent-navy text-white px-4 py-2 rounded hover:bg-accent-navy/80 transition-colors opacity-50"
        >
          View All Mobile App Designs
        </Link>
      </div>

      <Carousel className="w-full max-w-5xl mx-auto">
        <CarouselContent>
          {isLoading ? (
            Array(3).fill(0).map((_, index) => (
              <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                <div className="aspect-[9/16] rounded-xl overflow-hidden">
                  <Skeleton className="h-full w-full" />
                </div>
              </CarouselItem>
            ))
          ) : (
            designs.map((design) => (
              <CarouselItem key={design.id} className="md:basis-1/2 lg:basis-1/3">
                <Link to="/mobile-app-designs">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    viewport={{ once: true }}
                    className="relative aspect-[9/16] rounded-xl overflow-hidden cursor-pointer"
                  >
                    <img
                      src={design.url}
                      alt={design.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={handleImageError}
                    />
                  </motion.div>
                </Link>
              </CarouselItem>
            ))
          )}
        </CarouselContent>
        <CarouselPrevious className="hidden md:flex" />
        <CarouselNext className="hidden md:flex" />
      </Carousel>
    </section>
  );
};